// src/components/Login.tsx
import { useState } from "react";
import { Form, Input, Button, message, Radio } from "antd";
import { usePostData } from "../../Network/usePostData";
import "./style.scss";
import { AdminIcon, ProviderIcon, SponsorIcon } from "./LoginIcon/Index";
import { useTranslation } from "react-i18next";

interface LoginFormValues {
  email: string;
  password: string;
  role: string;
}

interface LoginResponse {
  data: {
    accessToken: string;
  };
}

const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const { mutateAsync } = usePostData<LoginResponse, LoginFormValues>({
    url: "auth/customer/login",
    options: {
      hideMessage: true,
    },
  });

  const { mutateAsync: providerLogin } = usePostData<
    LoginResponse,
    LoginFormValues
  >({
    url: "auth/service-provider/login",
    options: {
      hideMessage: true,
    },
  });

  const { mutateAsync: adminLogin } = usePostData<
    LoginResponse,
    LoginFormValues
  >({
    url: "auth/super-admin/login",
    options: {
      hideMessage: true,
    },
  });

  const { mutateAsync: sponsorLogin } = usePostData<
    LoginResponse,
    LoginFormValues
  >({
    url: "auth/sponsor/login",
    options: {
      hideMessage: true,
    },
  });

  const handleSubmit = async (values: LoginFormValues) => {
    try {
      setLoading(true);
      if (values?.role === "Super Admin") {
        const response = await adminLogin(values);
        localStorage.setItem("token", response?.data?.accessToken);
      } else if (values?.role === "Provider") {
        const response = await providerLogin(values);
        localStorage.setItem("token", response?.data?.accessToken);
      } else if (values?.role === "Sponsor") {
        const response = await sponsorLogin(values);
        localStorage.setItem("token", response?.data?.accessToken);
      } else {
        const response = await mutateAsync(values);
        localStorage.setItem("token", response?.data?.accessToken);
      }
      await i18n.changeLanguage("en");

      window.location.href = `${window.location.origin}/dashboard`;
      setLoading(false);
    } catch (error) {
      // message.error(t("login.failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-wrapper"
      style={{ backgroundImage: "url(/assets/main-bg.webp)" }}
    >
      <div className="login-wrapper-bg">
        <div className="login-logo">
          <img src="/assets/logo/logo.svg" alt="logo" className="logo" />
        </div>

        <div className="login-form-wrapper">
          <h2>{t("login.title")}</h2>

          <Form
            form={form}
            layout="vertical"
            initialValues={{ role: "Super Admin" }}
            onFinish={handleSubmit}
          >
            <p className="pointer-login-text">{t("login.chooseRole")}</p>
            <Form.Item label="" name="role" className="role-radio-box">
              <Radio.Group className="step-providers-wrapper">
                <Radio key="Super Admin" value="Super Admin">
                  <AdminIcon />
                  {t("login.admin")}
                </Radio>
                <Radio key="Provider" value="Provider">
                  <ProviderIcon />
                  {t("login.provider")}
                </Radio>
                <Radio key="Sponsor" value="Sponsor">
                  <SponsorIcon />
                  {t("login.sponsor")}
                </Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item label={t("login.emailLabel")} name="loginId" rules={[]}>
              <Input placeholder={t("login.emailPlaceholder")} />
            </Form.Item>

            <Form.Item
              label={t("login.passwordLabel")}
              name="password"
              rules={[{ message: t("login.passwordRequired") }]}
            >
              <Input.Password placeholder={t("login.passwordPlaceholder")} />
            </Form.Item>

            <p className="forget-text">{t("login.forgetPassword")}</p>

            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>
                {t("login.button")}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;
